import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom'; 
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { useGetUserSessionQuery } from '../../services/api/userApi';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useCreateCheckoutSessionMutation } from '../../services/api/appApi';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const GetStarted = () => {
    const stripe = useStripe();
    const { data: userSessionData, isLoading: isUserSessionLoading, isError: isUserSessionError, isFetching: isUserSessionFetching } = useGetUserSessionQuery();
    const isLoggedIn = userSessionData?.isLoggedIn;
    const [sessionLoading, setSessionLoading] = useState("");
    const navigate = useNavigate();
    const [createCheckoutSession, { isLoading: isCreatingCheckoutSession, isError: isCreateCheckoutSessionError }] = useCreateCheckoutSessionMutation();

    // Define tiers
    const tiers = [
        {   
            name: "Monthly",
            id: "tier-monthly",
            prod: "monthly",
            priceId: import.meta.env.VITE_STRIPE_MONTHLY,
            price: "$14.99",
            priceSuffix: "/month",
            description: "Unlock all features and unlimited access with no long-term commitment.",
            cycle: "billed monthly",
            btnText: isLoggedIn ? "Subscribe" : "Create an Account",
            features: [
                "Everything in Free",
                "Normalized Team Stats (League Rank, Z-Score, League Index, Min-Max)",
                "Unlimited Player Analysis",
                "Unlimited Game Analysis",
                "Unlimited Private Systems",
                "Publish Systems",
                "Follow Public Systems"
            ],
            mostPopular: false,
            isFree: false
        },
        {
            name: "Annual",
            id: "tier-annual",
            prod: "annual",
            priceId: import.meta.env.VITE_STRIPE_ANNUAL,
            price: "$119.99",
            priceSuffix: "/year",
            description: "Best value with 33% savings compared to month-to-month.",
            cycle: "billed annually",
            btnText: isLoggedIn ? "Subscribe" : "Create an Account",
            features: [
                "Everything in Free",
                "Normalized Team Stats (League Rank, Z-Score, League Index, Min-Max)",
                "Unlimited Player Analysis",
                "Unlimited Game Analysis",
                "Unlimited Private Systems",
                "Publish Systems",
                "Follow Public Systems",
                "33% off month-to-month pricing"
            ],
            mostPopular: true,
            isFree: false
        }
    ];

    const handleGetStarted = async (tier) => {
        // Track get started button click
        trackEvent(GA_EVENTS.CLICK_PRICING_TIER, {
            tier_name: tier.name,
            tier_type: tier.prod,
            price: tier.price,
            is_free: tier.isFree,
            user_logged_in: isLoggedIn,
            button_location: 'get_started_section'
        });

        // If free tier, navigate to signup (or dashboard if logged in)
        if (tier.isFree) {
            if (isLoggedIn) {
                navigate('/upcoming-games');
            } else {
                navigate('/signup');
            }
            return;
        }

        // If user is signed in - create checkout session for paid tiers
        if(isLoggedIn){
            setSessionLoading(tier.prod);
            const body = { 
                priceId: tier.priceId,
                successUrl: `${window.location.origin}/subscribed?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${window.location.origin}/get-started`,
                trial: false
            };
          
            try{
                const payload = await createCheckoutSession(body);
                if (payload.error) {
                    console.error(payload.error.data.error);
                } else {
                    const sessionId = payload.data.sessionId;
                    const result = await stripe.redirectToCheckout({ sessionId });
                    if (result.error) {
                        console.error(result.error.message);
                    }
                }
            } catch(error) {
                console.error('Error creating checkout session:', error.message);
            } finally {
                setSessionLoading("");
            }
            
            return;
        }

        // Navigate to create account page if not signed in
        navigate('/signup');
    };

    return (
        <div className="bg-white dark:bg-brand-dark-900 py-12 h-full overflow-y-auto">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-xl/8 font-semibold text-brand-red-500 dark:text-white">Get Started</h2>
                    <p className="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-900 dark:text-white sm:text-6xl">
                        Choose the plan that fits your needs
                    </p>
                </div>
                
                <div className="mx-auto mt-16 flex flex-wrap justify-center gap-8">
                    {tiers.map((tier) => (
                        <div
                            key={tier.id}
                            className={classNames(
                                tier.mostPopular 
                                    ? 'ring-2 ring-brand-red-500 dark:bg-white/5 bg-white' 
                                    : 'ring-1 ring-gray-200 bg-white dark:ring-white/10 dark:bg-transparent',
                                'rounded-3xl p-8 xl:p-10 shadow-lg dark:shadow-gray-800/10 w-full max-w-sm'
                            )}
                        >
                            <div className="flex flex-col h-full">
                                {/* Header section with fixed spacing */}
                                <div className="flex-none">
                                    <div className="flex items-center justify-between gap-x-4">
                                        <h3 id={tier.id} className="text-lg/8 font-semibold text-gray-900 dark:text-white">
                                            {tier.name}
                                        </h3>
                                        {tier.mostPopular && (
                                            <p className="rounded-full bg-brand-red-500 px-2.5 py-1 text-xs/5 font-semibold text-white">
                                                Best value
                                            </p>
                                        )}
                                        {tier.isFree && (
                                            <p className="rounded-full bg-brand-red-500 px-2.5 py-1 text-xs/5 font-semibold text-white">
                                                No card required
                                            </p>
                                        )}
                                    </div>
                                    <p className="mt-4 text-sm/6 text-pretty text-gray-600 dark:text-gray-300">{tier.description}</p>
                                    <p className="mt-6 flex items-baseline gap-x-1">
                                        <span className="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                            {tier.price}
                                        </span>
                                        <span className="text-sm/6 font-semibold text-gray-600 dark:text-gray-300">
                                            {tier.priceSuffix}
                                        </span>
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{tier.cycle}</p>
                                </div>

                                {/* Features list with flex-grow to fill space */}
                                <ul role="list" className="mt-8 flex-grow space-y-3 text-sm/6 text-gray-600 dark:text-gray-300">
                                    {tier.features.map((feature) => (
                                        <li key={feature} className="flex gap-x-3">
                                            <CheckIcon 
                                                aria-hidden="true" 
                                                className="h-6 w-5 flex-none text-brand-red-500 dark:text-white" 
                                            />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {/* Button section with fixed spacing */}
                                <div className="flex-none mt-8">
                                    <button
                                        onClick={() => handleGetStarted(tier)}
                                        disabled={sessionLoading === tier.prod}
                                        aria-describedby={tier.id}
                                        className={classNames(
                                            tier.mostPopular
                                                ? 'bg-brand-red-500 text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline-brand-red-500'
                                                : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:ring-0 dark:focus-visible:outline-white',
                                            'block w-full rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-colors'
                                        )}
                                    >
                                        {sessionLoading === tier.prod ? "Processing..." : tier.btnText}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default GetStarted;

